import {
  TaskCreate_FormSections,
  FNOL_RequestForm,
  FNOL_RequestFormDefaultValues,
  FNOL_RequestFormSchema,
  IngressRequestDefaultValues,
  IngressRequestForm,
  ResultStatus,
  AttributeOptions,
  IngressRequestFormSchema,
  QualifiedResourcePost,
  CWSSummary,
  FNOL_TaskTypes,
  QualifiedResourcesFNOLFilters,
  Severity,
} from '@dispatch/shared/types';
import { DrawerComponent, Form, RenderFuncProps } from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useMemo } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { Outlet, useNavigate } from 'react-router-dom';
import { Button, Stack } from '@mui/material';
import { IngressRequestSection } from './ingressRequestSection';
import { isEmpty, isNull, isUndefined } from 'lodash';
import { IngressRequestReviewSection } from './ingressRequestReviewSection';
import { QualifiedFNOLRequestSection } from './QualifiedFNOLRequestSection';
import { QualifiedResourcesListSection } from './QualifiedResourcesListSection';
import {
  PaginatedQueryResult,
  useCompany,
  useGeneralDataContext,
} from '@dispatch/shared/hooks';
import { useQueryClient } from 'react-query';

const LANGUAGE_SPOKEN = 'Languages Spoken';
const VIRTUAL = 'VIRTUAL';

export type QualifiedResourcesTaskCreateProps = {
  navigateToOnClose?: string;
  formSection: TaskCreate_FormSections;
  setFormSection: React.Dispatch<React.SetStateAction<TaskCreate_FormSections>>;
  fnolStatus: ResultStatus;
  qualifiedResources?: QualifiedResourcePost[];
  selectedResource: QualifiedResourcePost | null;
  setSelectedResource: React.Dispatch<
    React.SetStateAction<QualifiedResourcePost | null>
  >;
  companyAttributes: AttributeOptions[];
  onHandleFNOLRequestFormSubmit: (values: FNOL_RequestForm) => void;
  onHandleIngressRequestFormSubmit: (values: IngressRequestForm) => void;
  ingressStatus: ResultStatus;
  saveButtonLabel: string;
  setSaveButtonLabel: React.Dispatch<React.SetStateAction<string>>;
  timeZone?: string;
  setClaimNumberInput: React.Dispatch<React.SetStateAction<string>>;
  cwsSummary?: CWSSummary;
  refetchQualified?: () => void;
  setQualifiedResourcesFilters: React.Dispatch<
    React.SetStateAction<QualifiedResourcesFNOLFilters>
  >;
  qualifiedResourcesFilters: QualifiedResourcesFNOLFilters | null;
  qualifiedResourcesPaging?: PaginatedQueryResult<
    QualifiedResourcePost[],
    unknown
  >;
  cwsStatus?: ResultStatus;
};

export function QualifiedResourcesTaskCreate({
  navigateToOnClose = '/tasks',
  formSection,
  setFormSection,
  fnolStatus,
  qualifiedResources,
  selectedResource,
  setSelectedResource,
  setClaimNumberInput,
  companyAttributes,
  onHandleFNOLRequestFormSubmit,
  onHandleIngressRequestFormSubmit,
  ingressStatus,
  saveButtonLabel,
  setSaveButtonLabel,
  timeZone = '',
  cwsSummary,
  refetchQualified,
  qualifiedResourcesFilters,
  setQualifiedResourcesFilters,
  qualifiedResourcesPaging,
  cwsStatus,
}: QualifiedResourcesTaskCreateProps) {
  const navigate = useNavigate();
  const { statesOptions } = useGeneralDataContext();
  const { perilOptions } = useCompany();

  const preferredLanguages = useMemo(
    () =>
      companyAttributes
        .find((attribute) => attribute.title === LANGUAGE_SPOKEN)
        ?.attributeDefinitionValues.map((value) => ({
          title: value.title,
          value: value.value,
        })),
    [companyAttributes]
  );

  const renderBackButton = () => {
    return (
      <Stack>
        <Button
          type="button"
          variant="outlined"
          onClick={FNOL_FormSectionPrevious}
          disabled={formSection === TaskCreate_FormSections.INITIAL}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Back
        </Button>
      </Stack>
    );
  };

  const renderFormHelper = () => {
    return (
      <Stack>
        <Form<FNOL_RequestForm>
          formId="fnolRequestForm"
          errors={fnolRequestFormState.errors}
          control={fnolRequestFormControl}
          handleSubmit={fnolRequestHandleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <div style={{ width: '100%' }}>
                {renderFNOLRequestFormSection(control, errors)}
              </div>
            );
          }}
        />
      </Stack>
    );
  };

  const {
    control: fnolRequestFormControl,
    formState: fnolRequestFormState,
    handleSubmit: fnolRequestHandleSubmit,
    getValues: fnolRequestFormGetValues,
    setValue: fnolRequestFormSetValue,
    watch: fnolRequestFormWatch,
  } = useForm<FNOL_RequestForm>({
    resolver: yupResolver(FNOL_RequestFormSchema),
    defaultValues: {
      ...FNOL_RequestFormDefaultValues,
      severity: Severity.ZERO,
    },
  });

  const renderFNOLRequestFormSection = (
    control: Control<FieldValues>,
    errors: FieldErrors
  ) => {
    return (
      <QualifiedFNOLRequestSection
        cwsStatus={cwsStatus}
        control={control}
        errors={errors}
        companyAttributes={companyAttributes}
        watch={fnolRequestFormWatch}
        getValues={fnolRequestFormGetValues}
        setValue={fnolRequestFormSetValue}
        setClaimNumberInput={setClaimNumberInput}
      />
    );
  };

  const renderQualifiedResourcesListSection = () => {
    return (
      <QualifiedResourcesListSection
        taskType={fnolRequestFormGetValues('fnolTaskType')}
        qualifiedResources={qualifiedResources}
        status={qualifiedResourcesPaging?.status || 'idle'}
        qualifiedResourcesPaging={qualifiedResourcesPaging}
        qualifiedResourcesFilters={qualifiedResourcesFilters}
        setQualifiedResourcesFilters={setQualifiedResourcesFilters}
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
        timeZone={timeZone}
        getValues={fnolRequestFormGetValues}
      />
    );
  };

  const {
    control: ingressRequestFromControl,
    formState: ingressRequestFormState,
    handleSubmit: ingressRequestHandleSubmit,
    reset: ingressRequestReset,
    getValues: ingressRequestGetValues,
    setValue: ingressRequestFormSetValue,
    watch: ingressRequestFormWatch,
  } = useForm<IngressRequestForm>({
    defaultValues: {
      ...IngressRequestDefaultValues,
      assignmentHandling: {
        ...IngressRequestDefaultValues.assignmentHandling,
        severity: Severity.ZERO,
      },
    },
    resolver: yupResolver(IngressRequestFormSchema),
  });

  useEffect(() => {
    if (!isEmpty(selectedResource)) {
      ingressRequestFormSetValue('requiredResource', {
        title: selectedResource?.name || '',
        value: selectedResource?.id.toString() || '',
      });
    }
  }, [selectedResource]);

  useEffect(() => {
    if (isUndefined(cwsSummary)) return;
    const {
      claimContact: LossContact,
      peril,
      lossAddress,
      catastropheNumber,
      lossDate,
      creationDate,
    } = cwsSummary;

    const stateAbr = statesOptions.find(
      (state) => state.title === lossAddress.state
    )?.value;
    const perilValue = perilOptions?.find(
      (option) => option.title === peril
    )?.value;

    if (isNull(lossAddress)) return;

    ingressRequestFormSetValue(
      'claim.insuredName',
      `${LossContact?.firstName} ${LossContact?.lastName}` || ''
    );

    ingressRequestFormSetValue('contact.primary', {
      name: `${LossContact?.firstName} ${LossContact?.lastName}` || '',
      email: LossContact?.email || '',
      phone: LossContact?.primaryPhone || '',
      phone2: LossContact?.alternatePhone || '',
      contactMethod: '',
    });

    ingressRequestFormSetValue('claim.dateOfLoss', lossDate || '');
    ingressRequestFormSetValue('claim.dateOfNotice', creationDate || '');

    ingressRequestFormSetValue(
      'claim.claimNumber',
      cwsSummary?.claimNumber || ''
    );

    fnolRequestFormSetValue('lossLocation', {
      streetLine1: lossAddress.line1 || '',
      streetLine2: lossAddress.line2 || '',
      city: lossAddress.city || '',
      state: String(stateAbr) || '',
      zip: lossAddress.zipCode || '',
    });

    fnolRequestFormSetValue('timeZone', lossAddress?.timezone || '');

    fnolRequestFormSetValue('catCode', catastropheNumber || '');

    fnolRequestFormSetValue('peril', String(perilValue) || '');

    if (lossDate) {
      fnolRequestFormSetValue('dateOfLoss', new Date(lossDate));
    }
  }, [cwsSummary]);

  useEffect(() => {
    fnolRequestFormSetValue('methodOfInspection', 'FIELD');
    fnolRequestFormSetValue('fnolTaskType', 'Claim Owner' as FNOL_TaskTypes);
  }, []);

  useEffect(() => {
    if (
      ingressRequestGetValues('assignmentHandling.methodOfInspection') ===
      'FIELD'
    ) {
      ingressRequestFormSetValue('assignmentHandling.travel', true);
    }
  }, [
    ingressRequestFormSetValue,
    ingressRequestGetValues('assignmentHandling.methodOfInspection'),
  ]);

  const renderIngressSection = (
    control: Control<FieldValues>,
    errors: FieldErrors
  ) => {
    return (
      <IngressRequestSection
        control={control}
        errors={errors}
        getValues={ingressRequestGetValues}
        watch={ingressRequestFormWatch}
        companyAttributes={companyAttributes}
      />
    );
  };

  const renderIngressRequestReviewSection = () => {
    return (
      <IngressRequestReviewSection
        preferredLanguages={preferredLanguages}
        getValues={ingressRequestGetValues}
        timeZone={timeZone}
      />
    );
  };

  const fnolValues = fnolRequestFormGetValues();
  const ingressValues = ingressRequestGetValues();

  const copyFnolToIngress = () => {
    const newIngressValues: IngressRequestForm = {
      claim: {
        lossLocation: fnolValues.lossLocation,
        claimNumber: ingressValues.claim.claimNumber || '',
        insuredName: ingressValues.claim.insuredName || '',
        dateOfLoss: ingressValues.claim.dateOfLoss || '',
        dateOfNotice: ingressValues.claim.dateOfNotice || '',
        propertyType: ingressValues.claim.propertyType,
        peril: fnolValues.peril,
        catCode: fnolValues.catCode,
      },
      contact: {
        primary: ingressValues.contact.primary,
        hasAlternateLocation: ingressValues.contact.hasAlternateLocation,
        hasSecondaryContact: ingressValues.contact.hasSecondaryContact,
      },
      assignmentHandling: {
        travel: fnolValues.methodOfInspection === VIRTUAL ? false : true,
        templateId: ingressValues.assignmentHandling.templateId || '',
        durationInMinutes: fnolValues.durationInMinutes,
        priority: ingressValues.assignmentHandling.priority,
        severity: fnolValues.severity,
        methodOfInspection: fnolValues.methodOfInspection,
        mustEndBefore: new Date().toString(),
        mustStartAfter: new Date().toString(),
        notes: ingressValues.assignmentHandling.notes,
        preferredLanguage: fnolValues.preferredLanguage,
        assignmentCategory: fnolValues.assignmentCategory,
        taskType: fnolValues.fnolTaskType,
      },
      claimAttributes: fnolValues.claimAttributes,
      requiredResource: {
        value: selectedResource?.id || '',
        title: selectedResource?.name || '',
      },
    };

    ingressRequestReset(newIngressValues);
  };

  const queryClient = useQueryClient();

  const handleFnol = React.useCallback(() => {
    queryClient.invalidateQueries('resources-as-post');
    fnolRequestHandleSubmit(onHandleFNOLRequestFormSubmit)();
  }, [refetchQualified]);

  const FNOL_FormSectionProgressor = () => {
    switch (formSection) {
      case TaskCreate_FormSections.INITIAL:
        handleFnol();
        break;
      case TaskCreate_FormSections.QUALIFIED_RESOURCES:
        copyFnolToIngress();
        setFormSection(TaskCreate_FormSections.INGRESS);
        setSaveButtonLabel('Review');
        break;
      case TaskCreate_FormSections.INGRESS:
        ingressRequestHandleSubmit(onHandleIngressRequestFormSubmit)();

        break;
      case TaskCreate_FormSections.REVIEW:
        ingressRequestHandleSubmit(onHandleIngressRequestFormSubmit)();

        break;
      default:
        break;
    }
  };

  const FNOL_FormSectionPrevious = () => {
    switch (formSection) {
      case TaskCreate_FormSections.INGRESS:
        setSaveButtonLabel('Next');
        setFormSection(TaskCreate_FormSections.QUALIFIED_RESOURCES);
        break;

      case TaskCreate_FormSections.QUALIFIED_RESOURCES:
        setSaveButtonLabel('Next');
        setFormSection(TaskCreate_FormSections.INITIAL);
        setQualifiedResourcesFilters({} as QualifiedResourcesFNOLFilters);
        break;
      case TaskCreate_FormSections.REVIEW:
        setSaveButtonLabel('Review');
        setFormSection(TaskCreate_FormSections.INGRESS);
        break;
      default:
        break;
    }
  };

  const disableSaveButton = () => {
    return (
      formSection === TaskCreate_FormSections.QUALIFIED_RESOURCES &&
      isEmpty(selectedResource)
    );
  };

  const methodOfInspection = ingressRequestFormWatch(
    'assignmentHandling.methodOfInspection'
  );
  React.useEffect(() => {
    if (isUndefined(methodOfInspection)) {
      ingressRequestFormSetValue(
        'assignmentHandling.methodOfInspection',
        FNOL_TaskTypes.NON_COMPLEX
      );
    }
  }, [methodOfInspection]);

  return (
    <>
      <DrawerComponent
        onClose={() => {
          navigate(navigateToOnClose);
        }}
        onSave={FNOL_FormSectionProgressor}
        saveButtonLabel={saveButtonLabel}
        renderLeftSideButtons={[renderBackButton()]}
        showButtons
        saveDisabled={disableSaveButton()}
        sx={{
          width: '65vw',
        }}
      >
        {formSection === TaskCreate_FormSections.INITIAL && renderFormHelper()}
        {formSection === TaskCreate_FormSections.QUALIFIED_RESOURCES &&
          renderQualifiedResourcesListSection()}
        {formSection === TaskCreate_FormSections.INGRESS && (
          <Form<IngressRequestForm>
            formId="ingressRequestForm"
            errors={ingressRequestFormState.errors}
            control={ingressRequestFromControl}
            handleSubmit={ingressRequestHandleSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <div style={{ width: '100%' }}>
                  {renderIngressSection(control, errors)}
                </div>
              );
            }}
          />
        )}
        {formSection === TaskCreate_FormSections.REVIEW &&
          renderIngressRequestReviewSection()}
      </DrawerComponent>
      <Outlet />
    </>
  );
}
